import { useId, type Ref } from "react"
import cx from "clsx"
import { BINDINGS, isBoundField, type ModuleDefinitionField } from "shared"
import { fieldName } from "@rawa/form-modules/fields"
import { DragPreview } from "../../../dragAndDrop/DragPreview"
import { useSortableItem } from "../../../dragAndDrop/useSortableItem"
import sortable from "../../../dragAndDrop/sortable.module.css"
import { FORM_MODE } from "../utils/boundFields"
import { isFieldDrag, placedField, placedFieldZone } from "../utils/moduleDrag"
import LayersIcon from "../../../assets/icon-layers.svg?react"
import ArrowDownIcon from "../../../assets/icon-arrow-down.svg?react"
import ArrowUpIcon from "../../../assets/icon-arrow-up.svg?react"
import TrashIcon from "@rawa/ui/assets/icon-trash.svg?react"
import styles from "./PlacedFieldCard.module.css"

type FieldRowProps = {
  field: ModuleDefinitionField
  /** Whether its properties are the ones open. */
  selected: boolean
  needsAttention: boolean
  first: boolean
  last: boolean
  onSelect: () => void
  onMove: (by: -1 | 1) => void
  onRemove: () => void
}

/**
 * A field on a module's canvas: pressed to open its properties, with the
 * controls that move it and take it off. A field the save would refuse is
 * marked, as its description rather than its name, so it is still found by
 * its name.
 *
 * It can also be dragged up and down the canvas by its grip, as a copy of
 * itself that follows the pointer, and another field, or a control from the
 * library, dropped on its top or bottom edge; the screen decides what a drop
 * does (see eventForDrop). Only the grip starts a drag, so pressing the field
 * still selects it, and the Move buttons stay as the way to do the same from
 * a keyboard.
 */
export function PlacedFieldCard(props: FieldRowProps) {
  const item = useSortableItem<HTMLLIElement>({
    id: props.field.id,
    kind: placedField,
    zone: placedFieldZone,
    accepts: isFieldDrag,
  })

  return (
    <li
      ref={item.ref}
      className={cx(styles.fieldRow, sortable.item)}
      data-dragging={item.dragging || undefined}
      data-drop-edge={item.dropEdge ?? undefined}
    >
      <FieldRow {...props} handleRef={item.handleRef} />
      <DragPreview item={item} className={styles.fieldRow}>
        <FieldRow {...props} />
      </DragPreview>
    </li>
  )
}

/**
 * What a field's card shows, drawn once for the card and again for its copy
 * while it is dragged; each draws its own attention mark, with an id of its
 * own, so the two never share one.
 */
function FieldRow({
  field,
  selected,
  needsAttention,
  first,
  last,
  onSelect,
  onMove,
  onRemove,
  handleRef,
}: FieldRowProps & {
  /** The card's grip, as its drag handle; the copy's grip is only drawn. */
  handleRef?: Ref<HTMLSpanElement>
}) {
  const attentionId = useId()
  const name = fieldName(field)

  return (
    <>
      <span
        ref={handleRef}
        className={cx(styles.grip, sortable.handle)}
        data-testid="drag-handle"
        aria-hidden="true"
      >
        <GripIcon />
      </span>
      <button
        type="button"
        className={styles.field}
        aria-pressed={selected}
        aria-describedby={needsAttention ? attentionId : undefined}
        onClick={onSelect}
      >
        <span className={styles.fieldGlyph} aria-hidden="true">
          <LayersIcon />
        </span>
        {name}
        {isBoundField(field) && (
          <span className={styles.boundTag}>
            {FORM_MODE[BINDINGS[field.binding].formMode].tag}
          </span>
        )}
        {needsAttention && (
          <span
            id={attentionId}
            className={styles.attention}
            aria-hidden="true"
          >
            Needs attention
          </span>
        )}
      </button>
      <button
        type="button"
        className={styles.rowAction}
        aria-label={`Move ${name} up`}
        disabled={first}
        onClick={() => onMove(-1)}
      >
        <ArrowUpIcon aria-hidden="true" />
      </button>
      <button
        type="button"
        className={styles.rowAction}
        aria-label={`Move ${name} down`}
        disabled={last}
        onClick={() => onMove(1)}
      >
        <ArrowDownIcon aria-hidden="true" />
      </button>
      <button
        type="button"
        className={styles.remove}
        aria-label={`Remove ${name}`}
        onClick={onRemove}
      >
        <TrashIcon aria-hidden="true" />
      </button>
    </>
  )
}

/** Two columns of three dots: the usual sign of something to pick up. */
function GripIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="currentColor">
      {[5, 11].flatMap((x) =>
        [3, 8, 13].map((y) => (
          <circle key={`${x}-${y}`} cx={x} cy={y} r={1.5} />
        )),
      )}
    </svg>
  )
}
